import type Phaser from 'phaser';

export interface ZoneHelpCardOptions {
  scene: Phaser.Scene;
  /** Center anchor for the card. */
  cx: number;
  cy: number;
  width: number;
  dpr: number;
  onClose: () => void;
}

const CARD_BG_TOP = 0x0d2c1d;
const CARD_BG_BOT = 0x06140e;
const GOLD = 0xd4af37;

/**
 * Modal explanation card used by help mode. Renders a felt-dark gradient
 * card with a gold top border, a title, a body paragraph, and a single
 * "Got it" close button. Tapping the card or the button closes; tapping
 * outside is the caller's responsibility.
 */
export class ZoneHelpCard {
  private readonly scene: Phaser.Scene;
  private readonly dpr: number;
  private readonly cx: number;
  private readonly cy: number;
  private readonly width: number;
  private readonly objects: Phaser.GameObjects.GameObject[] = [];
  private titleText!: Phaser.GameObjects.Text;
  private bodyText!: Phaser.GameObjects.Text;

  // Layout pieces stashed during draw(), positioned during relayout()
  private bg!: Phaser.GameObjects.Graphics;
  private buttonBg!: Phaser.GameObjects.Graphics;
  private hitRect!: Phaser.GameObjects.Rectangle;
  private buttonText!: Phaser.GameObjects.Text;
  // Padding + button-size constants — set once in draw(), never reassigned.
  private padX = 0;
  private padY = 0;
  private buttonW = 0;
  private buttonH = 0;

  constructor(opts: ZoneHelpCardOptions) {
    this.scene = opts.scene;
    this.dpr = opts.dpr;
    this.cx = opts.cx;
    this.cy = opts.cy;
    this.width = opts.width;
    this.draw(opts.onClose);
  }

  setContent(title: string, body: string) {
    this.titleText.setText(title);
    this.bodyText.setText(body);
    this.relayout();
  }

  setDepth(d: number) {
    // Three layers so the card bg never covers the body text or the
    // button. Card bg → body/button bg → text/hit-rect.
    this.bg.setDepth(d);
    this.titleText.setDepth(d + 1);
    this.bodyText.setDepth(d + 1);
    this.buttonBg.setDepth(d + 1);
    this.buttonText.setDepth(d + 2);
    this.hitRect.setDepth(d + 2);
  }

  destroy() {
    for (const obj of this.objects) obj.destroy();
    this.objects.length = 0;
  }

  private px(n: number) {
    return Math.round(n * this.dpr);
  }

  private draw(onClose: () => void) {
    const padX = this.px(16);
    const padY = this.px(14);

    this.titleText = this.scene.add
      .text(0, 0, '', {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${this.px(15)}px`,
        color: '#f4d35e',
        fontStyle: 'bold',
        wordWrap: { width: this.width - padX * 2 },
      })
      .setOrigin(0, 0);

    this.bodyText = this.scene.add
      .text(0, 0, '', {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${this.px(13)}px`,
        color: '#e6e6dc',
        wordWrap: { width: this.width - padX * 2 },
      })
      .setOrigin(0, 0);

    const buttonH = this.px(34);
    const buttonW = this.px(96);
    const buttonText = this.scene.add
      .text(0, 0, 'GOT IT', {
        fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
        fontSize: `${this.px(13)}px`,
        color: '#0d2c1d',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    const buttonBg = this.scene.add.graphics();
    const hitRect = this.scene.add
      .rectangle(0, 0, buttonW, buttonH, 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    hitRect.on('pointerdown', onClose);
    // Hover/press feedback: hover lifts the button slightly (1.0 → 0.85
    // alpha pair on bg + text), pointer-up after a click also restores —
    // though the card destroys on click so the press state is fleeting.
    hitRect.on('pointerover', () => {
      buttonBg.setAlpha(0.85);
      buttonText.setAlpha(0.85);
    });
    hitRect.on('pointerout', () => {
      buttonBg.setAlpha(1);
      buttonText.setAlpha(1);
    });

    const bg = this.scene.add.graphics();

    this.objects.push(bg, this.titleText, this.bodyText, buttonBg, hitRect, buttonText);

    this.bg = bg;
    this.buttonBg = buttonBg;
    this.hitRect = hitRect;
    this.buttonText = buttonText;
    this.padX = padX;
    this.padY = padY;
    this.buttonW = buttonW;
    this.buttonH = buttonH;
  }

  private relayout() {
    const padX = this.padX;
    const padY = this.padY;
    const titleH = this.titleText.height;
    const bodyH = this.bodyText.height;
    const buttonGap = this.px(14);
    const buttonH = this.buttonH;
    const buttonW = this.buttonW;
    const totalH = padY + titleH + this.px(8) + bodyH + buttonGap + buttonH + padY;
    const x0 = this.cx - this.width / 2;
    const y0 = this.cy - totalH / 2;

    this.bg.clear();
    this.bg.fillGradientStyle(CARD_BG_TOP, CARD_BG_TOP, CARD_BG_BOT, CARD_BG_BOT, 1);
    this.bg.fillRoundedRect(x0, y0, this.width, totalH, this.px(10));
    this.bg.lineStyle(this.px(1), GOLD, 1);
    this.bg.strokeRoundedRect(x0, y0, this.width, totalH, this.px(10));

    this.titleText.setPosition(x0 + padX, y0 + padY);
    this.bodyText.setPosition(x0 + padX, y0 + padY + titleH + this.px(8));

    const buttonX = this.cx - buttonW / 2;
    const buttonY = y0 + totalH - padY - buttonH;
    this.buttonBg.clear();
    this.buttonBg.fillStyle(GOLD, 1);
    this.buttonBg.fillRoundedRect(buttonX, buttonY, buttonW, buttonH, this.px(8));
    this.hitRect.setPosition(buttonX + buttonW / 2, buttonY + buttonH / 2);
    this.hitRect.setSize(buttonW, buttonH);
    this.buttonText.setPosition(buttonX + buttonW / 2, buttonY + buttonH / 2);
  }
}
